// tallyService.ts
// Service to sync Tally inventory data via Flask gateway server
import { collection, getDocs, query, where, writeBatch, serverTimestamp, doc } from 'firebase/firestore';
import { db, getTallyDb } from './firebase';

// Test mode override flag
let _testModeOverride = false;

// Get appropriate Firestore instance based on test mode
const getTargetDb = () => {
  if (_testModeOverride) return db; // Use primary (TEST) Firebase
  return getTallyDb(); // Use Tally (LIVE) Firebase
};

interface TallySyncResponse {
  success: boolean;
  message?: string;
  error?: string;
  summary?: {
    tally_records_found: number;
    deleted_count: number;
    inserted_count: number;
  };
  timestamp: string;
}

interface TallyHealthResponse {
  status: 'healthy' | 'unhealthy';
  services: {
    tally: 'connected' | 'disconnected' | 'error';
    firebase: 'connected' | 'disconnected';
  };
  error?: string;
  timestamp: string;
}

// Gateway server URL (local Flask server)
// Priority:
// 1) REACT_APP_TALLY_GATEWAY_URL (set per env)
// 2) Match page scheme to avoid mixed-content blocks (falls back to localhost:8080/8081)
const ENV_GATEWAY_URL = (process.env.REACT_APP_TALLY_GATEWAY_URL || '').trim();
const ENV_GATEWAY_TEST_URL = (process.env.REACT_APP_TALLY_GATEWAY_TEST_URL || '').trim();

// Helper to get gateway URL based on test mode
const getGatewayUrl = (useTestMode: boolean = false): string => {
  if (useTestMode && ENV_GATEWAY_TEST_URL) return ENV_GATEWAY_TEST_URL;
  if (!useTestMode && ENV_GATEWAY_URL) return ENV_GATEWAY_URL;
  
  // Default ports: 8080 for LIVE, 8081 for TEST
  const port = useTestMode ? '8081' : '8080';
  const host = `localhost:${port}`;
  
  if (typeof window !== 'undefined' && window.location?.protocol === 'https:') {
    return `https://${host}`;
  }
  return `http://${host}`;
};

/**
 * Sync inventory data from Tally to Firebase
 * This replaces the old fetchTallyStock function
 * @param company Tally company name to sync from
 * @param useTestMode If true, routes to TEST gateway (port 8081)
 */
export async function syncTallyInventory(company?: string, useTestMode: boolean = false): Promise<TallySyncResponse> {
  try {
    const gatewayUrl = getGatewayUrl(useTestMode);
    const response = await fetch(`${gatewayUrl}/api/tally-sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ company }),
    });

    const data: TallySyncResponse = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return data;
  } catch (error: any) {
    // Handle network errors and other exceptions
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      const gatewayUrl = getGatewayUrl(_testModeOverride);
      throw new Error(`Cannot connect to Tally Gateway Server at ${gatewayUrl}. Please ensure the Flask server is running.`);
    }
    
    throw new Error(`Tally sync failed: ${error.message}`);
  }
}

/**
 * Check health status of Tally and Firebase connections
 */
export async function checkTallyHealth(): Promise<TallyHealthResponse> {
  try {
    const gatewayUrl = getGatewayUrl(false); // Check LIVE gateway by default
    const response = await fetch(`${gatewayUrl}/api/health`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const data: TallyHealthResponse = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return data;
  } catch (error: any) {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      const gatewayUrl = getGatewayUrl(false); // Default to LIVE gateway for health check
      throw new Error(`Cannot connect to Tally Gateway Server at ${gatewayUrl}. Please ensure the Flask server is running.`);
    }
    
    throw new Error(`Health check failed: ${error.message}`);
  }
}

/**
 * Legacy function name for backward compatibility
 * @deprecated Use syncTallyInventory() instead
 */
export async function fetchTallyStock(): Promise<any[]> {
  console.warn('fetchTallyStock() is deprecated. Use syncTallyInventory() instead.');
  
  const result = await syncTallyInventory();
  
  if (!result.success) {
    throw new Error(result.error || 'Tally sync failed');
  }
  
  // Return empty array since we're now syncing directly to Firebase
  // The calling code should reload data from Firebase instead
  return [];
}

// ==========================
// ERP Push: Sales & Replace
// ==========================

export interface TallyPushLine {
  productId: string;
  productName: string;
  quantity: number; // positive numbers reduce stock
  unit: string;
  rate?: number;
  type: 'sale' | 'replace';
}

export interface TallyPushHeaderPayload {
  idempotencyKey: string; // salesHeader.transactionId
  date: string; // YYYY-MM-DD
  warehouseId?: string;
  routeId: string;
  outletId: string;
  outletName: string;
  salesmanId: string;
  salesmanName: string;
  invoiceNo: string;
  total: number;
  lines: TallyPushLine[];
}

export interface TallyPushResult {
  success: boolean;
  idempotencyKey: string;
  erpReferenceId?: string; // Tally voucher id/number
  error?: string;
  debug?: any;
}

/**
 * Push a batch of sales headers to Tally. The gateway must be idempotent.
 * @param company Tally company name to push to
 * @param useTestMode If true, routes to TEST gateway (port 8081)
 */
export async function pushSalesBatchToTally(headers: TallyPushHeaderPayload[], company?: string, useTestMode: boolean = false): Promise<TallyPushResult[]> {
  const gatewayUrl = getGatewayUrl(useTestMode);
  const res = await fetch(`${gatewayUrl}/api/push-sales`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ headers, company, debug: true })
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data?.error || `HTTP ${res.status}`);
  }
  return data?.results as TallyPushResult[];
}

// ==========================
// Orchestrator
// ==========================

/**
 * Orchestrate: push unsynced sales/replace to Tally then pull fresh stock per warehouse.
 * Returns a summary for UI.
 * @param useTestMode If true, uses TEST Firebase instead of LIVE Firebase
 */
export async function pushUnsyncedSalesThenPull(warehouseId?: string, maxBatch = 20, useTestMode = false): Promise<{
  pushed: number;
  succeeded: number;
  failed: number;
  pulled: boolean;
  pullSummary?: TallySyncResponse;
  pushDebug?: string[];
  rawResults?: TallyPushResult[];
}> {
  _testModeOverride = useTestMode;
  try {
    // 0) Prefer aggregated settlements: process ALL unsynced completed/reconciled settlements per day for this warehouse
    const today = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const todayStr = ymd(today);

  if (warehouseId) {
    const { headersToPush, idKeyToSettlementIds } = await buildAggregatedTallyHeaders(warehouseId, todayStr);

    if (headersToPush.length > 0) {
      const companyName = useTestMode ? 'GrowersCrate Test' : 'GrowersCrate Agrotech Pvt Ltd';
      const results = await pushSalesBatchToTally(headersToPush, companyName, useTestMode);
      let succeeded = 0; let failed = 0;
      const targetDb = getTargetDb();
      const batch = writeBatch(targetDb);
      results.forEach(r => {
        if (r.success) {
          succeeded++;
          const ids = idKeyToSettlementIds.get(r.idempotencyKey) || [];
          ids.forEach(sid => {
            batch.update(doc(targetDb, 'settlements', sid), {
              syncedWithERP: true,
              erpInvoiceId: r.erpReferenceId || headersToPush.find(h => h.idempotencyKey === r.idempotencyKey)?.invoiceNo,
              updatedAt: serverTimestamp(),
            });
          });
        } else {
          failed++;
        }
      });
      if (succeeded > 0) await batch.commit();

      const pulled = succeeded > 0 && failed === 0;
      const pushDebug = results.map(r => {
        const d = (r as any).debug || {};
        const errs = r.error ? `, error=${r.error}` : '';
        const stats = ((r as any).created !== undefined || (r as any).altered !== undefined || (r as any).errors !== undefined)
          ? `created=${(r as any).created ?? '?'}, altered=${(r as any).altered ?? '?'}, errors=${(r as any).errors ?? '?'}`
          : 'no-stats';
        const godown = d.godown ? `, godown=${d.godown}` : '';
        const vnum = d.voucherNo ? `, vch=${d.voucherNo}` : '';
        const company = d.company ? `, cmp=${d.company}` : '';
        const resp = d.responseSnippet ? `, resp=${String(d.responseSnippet).replace(/\s+/g,' ').slice(0,180)}` : '';
        const req = d.requestSnippet ? `, req=${String(d.requestSnippet).replace(/\s+/g,' ').slice(0,120)}` : '';
        return `${r.idempotencyKey}: ${stats}${godown}${vnum}${company}${errs}${resp}${req}`;
      });
      let pullSummary: TallySyncResponse | undefined = undefined;
      if (pulled) {
        const companyName = useTestMode ? 'GrowersCrate Test' : 'GrowersCrate Agrotech Pvt Ltd';
        pullSummary = await syncTallyInventory(companyName, useTestMode);
      }

      return { pushed: headersToPush.length, succeeded, failed, pulled, pullSummary, pushDebug, rawResults: results };
    }
    // If no lines across any date, continue to pull-only
  }

  // No aggregated settlements to push (or no warehouse provided): just pull
  const companyName = useTestMode ? 'GrowersCrate Test' : 'GrowersCrate Agrotech Pvt Ltd';
  const pullRes = await syncTallyInventory(companyName, useTestMode);
  return { pushed: 0, succeeded: 0, failed: 0, pulled: true, pullSummary: pullRes };
  } finally {
    _testModeOverride = false; // Always reset test mode
  }
}

// Build aggregated headers for a given warehouse across all unsynced completed/reconciled settlements, grouped by date
export async function buildAggregatedTallyHeaders(warehouseId: string, fallbackDate?: string): Promise<{ headersToPush: TallyPushHeaderPayload[]; idKeyToSettlementIds: Map<string, string[]>; }>{
  const today = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const todayStr = fallbackDate || ymd(today);

  const targetDb = getTargetDb();
  const settlementsQ = query(
    collection(targetDb, 'settlements'),
    where('parentWarehouseId', '==', warehouseId),
    where('status', 'in', ['completed', 'reconciled'])
  );
  const settlementsSnap = await getDocs(settlementsQ);
  const unsyncedSettlements = settlementsSnap.docs
    .map(d => ({ id: d.id, ...(d.data() as any) }))
    .filter(s => !s.syncedWithERP);

  const headersToPush: TallyPushHeaderPayload[] = [];
  const idKeyToSettlementIds = new Map<string, string[]>();

  if (unsyncedSettlements.length === 0) {
    return { headersToPush, idKeyToSettlementIds };
  }

  type AggKey = string;
  const byDate = new Map<string, any[]>();
  unsyncedSettlements.forEach(s => {
    const d = s.settlementDate || todayStr;
    const arr = byDate.get(d) || [];
    arr.push(s);
    byDate.set(d, arr);
  });

  byDate.forEach((list: any[], dateStr: string) => {
    const saleAgg = new Map<AggKey, { productId: string; productName: string; unit: string; qty: number; amount: number; }>();
    const replaceAgg = new Map<AggKey, { productId: string; productName: string; unit: string; qty: number; amount: number; }>();
    for (const s of list) {
      const sales: any[] = Array.isArray(s.salesRecords) ? s.salesRecords : [];
      const replaces: any[] = Array.isArray((s as any).replaceRecords) ? (s as any).replaceRecords : [];
      // Aggregate sales
      for (const rec of sales) {
        const items: any[] = Array.isArray(rec.items) ? rec.items : [];
        for (const it of items) {
          const productId = it.productId;
          const productName = it.productName || productId;
          const unit = (it.unit || 'Pckts').trim();
          const qty = Number(it.quantity || 0);
          const amt = Number(it.totalAmount || (Number(it.rate || 0) * qty));
          if (!productId || qty <= 0) continue;
          const key: AggKey = `${productId}__${unit}`;
          const prev = saleAgg.get(key) || { productId, productName, unit, qty: 0, amount: 0 };
          prev.qty += qty;
          prev.amount += amt;
          saleAgg.set(key, prev);
        }
      }
      // Aggregate replacements
      for (const rec of replaces) {
        const items: any[] = Array.isArray(rec.items) ? rec.items : [];
        for (const it of items) {
          const productId = it.productId;
          const productName = it.productName || productId;
          const unit = (it.unit || 'Pckts').trim();
          const qty = Number(it.quantity || 0);
          const amt = Number(it.totalAmount || (Number(it.rate || 0) * qty));
          if (!productId || qty <= 0) continue;
          const key: AggKey = `${productId}__${unit}`;
          const prev = replaceAgg.get(key) || { productId, productName, unit, qty: 0, amount: 0 };
          prev.qty += qty;
          prev.amount += amt;
          replaceAgg.set(key, prev);
        }
      }
    }

    const saleLines: TallyPushLine[] = Array.from(saleAgg.values()).map(a => ({
      productId: a.productId,
      productName: a.productName,
      quantity: a.qty,
      unit: a.unit,
      rate: a.qty > 0 ? a.amount / a.qty : 0,
      type: 'sale'
    }));
    const replaceLines: TallyPushLine[] = Array.from(replaceAgg.values()).map(a => ({
      productId: a.productId,
      productName: a.productName,
      quantity: a.qty,
      unit: a.unit,
      rate: a.qty > 0 ? a.amount / a.qty : 0,
      type: 'replace'
    }));

    if (saleLines.length > 0 || replaceLines.length > 0) {
      const idKey = `SETTLEMENT-${warehouseId}-${dateStr}`;
      const header: TallyPushHeaderPayload = {
        idempotencyKey: idKey,
        date: dateStr,
        warehouseId,
        routeId: 'AGGREGATED',
        outletId: 'CASH',
        outletName: 'Daily Cash Sales',
        salesmanId: 'SYSTEM',
        salesmanName: 'System',
        invoiceNo: `SL-${warehouseId}-${dateStr}`,
        total: [...saleLines, ...replaceLines].reduce((s, l) => s + (l.rate || 0) * l.quantity, 0),
        lines: [...saleLines, ...replaceLines]
      };
      headersToPush.push(header);
      idKeyToSettlementIds.set(idKey, list.map((s: any) => s.id));
    }
  });

  return { headersToPush, idKeyToSettlementIds };
}

// ==========================
// Utilities: Backfill ERP fields on headers
// ==========================

/**
 * Backfill ERP fields (erpSystem, erpSyncStatus, txnType, warehouseId) on salesHeaders for a given date.
 * Useful when some headers were created before the code update.
 */
export async function backfillErpFieldsForDate(date: string): Promise<{ processed: number; updated: number; }>{
  // Load all headers for the date
  const targetDb = getTargetDb();
  const snap = await getDocs(query(collection(targetDb, 'salesHeaders'), where('date', '==', date), where('status', '==', 'completed')));
  if (snap.empty) return { processed: 0, updated: 0 };
  const headers = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));

  // Preload routes for warehouseId mapping
  const routeIds = Array.from(new Set(headers.map(h => h.routeId).filter(Boolean)));
  const routesSnap = await getDocs(query(collection(targetDb, 'routes'), where('routeId', 'in', routeIds.slice(0, 10))));
  const routeMap = new Map<string, any>();
  routesSnap.docs.forEach(r => { const v = r.data() as any; if (v.routeId) routeMap.set(v.routeId, v); });

  let updated = 0;
  const batch = writeBatch(targetDb);
  for (const h of headers) {
    const needsErp = !('erpSyncStatus' in h) || !('erpSystem' in h) || !('txnType' in h) || !('warehouseId' in h);
    if (!needsErp) continue;

    // Compute txnType from details
    const detailsSnap = await getDocs(query(collection(targetDb, 'salesDetails'), where('transactionId', '==', h.transactionId)));
    const anySale = detailsSnap.docs.some(d => (d.data() as any).saleQty > 0);
    const anyReplace = detailsSnap.docs.some(d => (d.data() as any).replaceQty > 0);
    const txnType = anySale && anyReplace ? 'sale+replace' : anySale ? 'sale' : anyReplace ? 'replace' : undefined;

    const route = routeMap.get(h.routeId);
    const warehouseId = h.warehouseId || route?.warehouseId;

    const ref = doc(targetDb, 'salesHeaders', h.id);
    batch.update(ref, {
      erpSystem: 'tally',
      erpSyncStatus: (anySale || anyReplace) ? 'pending' : h.erpSyncStatus || 'pending',
      erpSyncAttempts: h.erpSyncAttempts || 0,
      txnType: txnType || h.txnType || null,
      warehouseId: warehouseId || null,
      erpSyncLastTriedAt: serverTimestamp(),
    });
    updated++;
  }
  if (updated > 0) await batch.commit();
  return { processed: headers.length, updated };
}
